import * as React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	UserProfileParameters,
	NewTravellerParameters,
	useGetTravellersQuery,
	useDefaultTravellerFieldsQuery,
	useDeleteTravellerMutation,
	useLocationsQuery
} from '@websky/graphql';
import { TravellerTypes } from '../../Traveller/types';
import { useTheme } from '../../theme';
import { useCreateTraveller, useSubmitPersonalDetails, useUpdateTraveller } from '../hooks';
import Traveller from '../../Traveller';
import Loader from '../../Loader';
import { useIsTablet } from '../../utils';

export enum renderType {
	Passengers,
	Companions
}

interface Props {
	format: renderType;
}

const TravelCompanions: React.FunctionComponent<Props> = ({ format }) => {
	const { t } = useTranslation('Account');
	const { TravelCompanions: theme } = useTheme('TravelCompanions');
	const { Account: accountTheme } = useTheme('AccountModule');
	const isTablet = useIsTablet();

	const [expanded, setExpanded] = useState<number | false>(false);
	const [deletingTravellerId, setDeletingTravellerId] = useState<string | null>(null);

	const { data: travellerFieldsData, loading: travellerFieldsLoading } = useDefaultTravellerFieldsQuery();
	const { data: companionsData, refetch: refetchCompanions, loading: companionsLoading } = useGetTravellersQuery();
	const { data: locationsData, loading: locationsLoading } = useLocationsQuery({
		variables: {
			parameters: {
				query: ''
			}
		}
	});

	const cities = React.useMemo(
		() => locationsData?.LocationSuggestions?.reduce((cities, location) => [...cities, ...location.cities], []),
		[locationsData]
	);

	const { submit: submitPersonalDetails } = useSubmitPersonalDetails(companionsData?.CurrentUser?.userProfile);
	const { submit: createNewCompanion } = useCreateTraveller();
	const { submit: updateTravellerSubmit } = useUpdateTraveller();
	const [deleteTravellerProfile] = useDeleteTravellerMutation();

	const refetchCompanionsAfterRequest = async (request: () => Promise<boolean>) => {
		try {
			const result = await request();

			if (result) {
				await refetchCompanions();
			}

			return result;
		} catch {
			return false;
		}
	};

	const onSubmitPersonalDetails = async (values: UserProfileParameters) => {
		return refetchCompanionsAfterRequest(async () => {
			const { data } = await submitPersonalDetails(values);

			return data?.UpdateUserProfile;
		});
	};

	const onCreateNewCompanion = async (values: object) => {
		return refetchCompanionsAfterRequest(async () => {
			const { data } = await createNewCompanion(values);
			const result = data?.CreateTravellerProfile;

			if (result) {
				setTimeout(() => {
					setExpanded(false);
				}, 1000);
			}

			return result;
		});
	};

	const onUpdateTraveller = (id: string) => async (values: NewTravellerParameters) => {
		return refetchCompanionsAfterRequest(async () => {
			const { data } = await updateTravellerSubmit(values, id);

			return data?.UpdateTravellerProfile;
		});
	};

	const onDeleteTraveller = async (id: string) => {
		setExpanded(false);
		setDeletingTravellerId(id);

		try {
			return await refetchCompanionsAfterRequest(async () => {
				const { data } = await deleteTravellerProfile({ variables: { id } });

				return data?.DeleteTravellerProfile;
			});
		} finally {
			setDeletingTravellerId(null);
		}
	};

	const onChangeExpended = useCallback(
		(index: number) => (isExpended: boolean) => {
			setExpanded(isExpended ? index : false);
		},
		[]
	);

	const expendedControlledProps = useCallback(
		(index: number) => ({
			expanded: expanded === index,
			handleChange: onChangeExpended(index)
		}),
		[expanded]
	);

	const isLoading = companionsLoading || travellerFieldsLoading || locationsLoading;

	if (isLoading) {
		return <Loader className={theme.loader} />;
	}

	return (
		<>
			{companionsData?.CurrentUser?.travellerProfiles && travellerFieldsData?.DefaultTravellerProfile && (
				<div className={theme.companions}>
					{format === renderType.Passengers && (
						<Traveller
							type={TravellerTypes.PersonalDetails}
							traveller={{ ...companionsData.CurrentUser.userProfile, canBeDeleted: false }}
							cities={cities}
							onSubmit={onSubmitPersonalDetails}
							{...expendedControlledProps(0)}
						/>
					)}

					{format === renderType.Companions && (
						<>
							{!isTablet && <h1 className={accountTheme.route__title}>{t('Travel companions')}</h1>}
							<div className={theme.description}>
								<p>{t('Usually traveling with a friend or family?')}</p>
								<p>{t('Add them to your companions list and make checking-in altogether easier.')}</p>
							</div>

							<div className={theme.companions__added}>
								<Traveller
									type={TravellerTypes.CompanionEmpty}
									traveller={travellerFieldsData.DefaultTravellerProfile}
									onSubmit={onCreateNewCompanion}
									{...expendedControlledProps(0)}
								/>

								{companionsData.CurrentUser.travellerProfiles.map((traveller, index) => (
									<Traveller
										key={traveller.id}
										type={TravellerTypes.CompanionFilled}
										traveller={traveller}
										onSubmit={onUpdateTraveller(traveller.id)}
										onDelete={onDeleteTraveller}
										disabled={deletingTravellerId === traveller.id}
										{...expendedControlledProps(index + 1)}
									/>
								))}
							</div>
						</>
					)}
				</div>
			)}
		</>
	);
};

export default TravelCompanions;
